import React from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  History,
  ListTree,
  Pencil,
  RotateCcw,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import IconMegaphone from "@components/CustomIcons/IconMegaphone";
import ListItemCardCst from "@components/ListItemCardCst";
import {
  ListItemAction,
  ListItemBadge,
} from "@components/ListItemCardCst/types";
import { EnrolledUserItem } from "../types";
import { getIssuedCertificateIds } from "../hooks/useRefreshCertificates";
import { formatEnrollTypeLabel } from "../config/enrollTypeMap";
import { formatEnrollmentDate } from "../config/enrollmentDates";
import {
  enrollmentStatusVariant,
  resolveEnrollmentStatusKey,
} from "../config/enrollmentStatus";
import { buildExpirationBadge } from "../config/expirationBadge";
import { ActivityV1Type } from "../../AddActivity/types";

interface UserEnrollmentRowProps {
  user: EnrolledUserItem;
  activityType?: ActivityV1Type;
  onDetails?: (user: EnrolledUserItem) => void;
  onEditEnrollment?: (user: EnrolledUserItem) => void;
  onDeleteEnrollment?: (user: EnrolledUserItem) => void;
  onEditGroupEnrollment?: (user: EnrolledUserItem) => void;
  onDeleteGroupEnrollment?: (user: EnrolledUserItem) => void;
  onHistory?: (user: EnrolledUserItem) => void;
  onDownloadCertificate?: (user: EnrolledUserItem) => void;
  onRefreshCertificate?: (user: EnrolledUserItem) => void;
  onSolicit?: (user: EnrolledUserItem) => void;
  onViewProgress?: (user: EnrolledUserItem) => void;
  // false per nascondere "Scarica certificato" (es. preiscritti, che non
  // hanno un certificato emesso).
  showCertificateAction?: boolean;
  showExpirationBadge?: boolean;
  // S05 (regola unica): le azioni previste ma non consentite dai permessi
  // vengono mostrate disabilitate, non nascoste.
  disabledActions?: EnrollmentRowDisabledActions;
}

export type EnrollmentRowActionKey =
  | "edit"
  | "delete"
  | "editGroup"
  | "deleteGroup"
  | "history"
  | "certificate"
  | "refreshCertificate"
  | "solicit"
  | "progress";

export type EnrollmentRowDisabledActions = Partial<
  Record<EnrollmentRowActionKey, boolean>
>;

const UserEnrollmentRow: React.FC<UserEnrollmentRowProps> = ({
  user,
  activityType,
  onDetails,
  onEditEnrollment,
  onDeleteEnrollment,
  onEditGroupEnrollment,
  onDeleteGroupEnrollment,
  onHistory,
  onDownloadCertificate,
  onRefreshCertificate,
  onSolicit,
  onViewProgress,
  showCertificateAction = true,
  showExpirationBadge = false,
  disabledActions = {},
}) => {
  const { t } = useTranslation("users-and-groups");
  const fullName =
    [user.userFirstName, user.userLastName].filter(Boolean).join(" ").trim() ||
    "-";

  const statusKey = resolveEnrollmentStatusKey(user);
  const expirationBadge = showExpirationBadge
    ? buildExpirationBadge(user, String(t("card.expiration-badge.expired")))
    : null;

  const badges: ListItemBadge[] = [
    { label: formatEnrollTypeLabel(user.enrollType) },
    {
      label: String(t(`card.status.${statusKey}`)),
      variant: enrollmentStatusVariant(statusKey),
    },
    ...(expirationBadge ? [expirationBadge] : []),
    ...(user.mandatory
      ? [
          {
            variant:
              expirationBadge?.variant === "error"
                ? ("error" as const)
                : ("warning" as const),
            icon: <TriangleAlert size={14} />,
            label: "",
          },
        ]
      : []),
  ];

  const fromGroup = user.massiveGroupId != null;

  const solicitActions: ListItemAction[][] = onSolicit
    ? [
        [
          {
            label: String(t("card.actions.solicit")),
            icon: <IconMegaphone size={14} />,
            onClick: () => onSolicit(user),
            disabled: statusKey === "completed" || !!disabledActions.solicit,
          },
        ],
      ]
    : [];

  const progressActions: ListItemAction[][] = onViewProgress
    ? [
        [
          {
            label: String(t("action", { ns: "enrollment-progress" })),
            icon: <ListTree size={14} />,
            onClick: () => onViewProgress(user),
            disabled: !!disabledActions.progress,
          },
        ],
      ]
    : [];

  const enrollmentActions: ListItemAction[][] =
    onDeleteEnrollment || onEditEnrollment
      ? [
          [
            {
              label: String(t("card.actions.edit-enrollment")),
              icon: <Pencil size={14} />,
              onClick: () => onEditEnrollment?.(user),
              disabled: !onEditEnrollment || !!disabledActions.edit,
            },
            {
              label: String(t("card.actions.delete-enrollment")),
              icon: <Trash2 size={14} />,
              onClick: () => onDeleteEnrollment?.(user),
              disabled:
                !onDeleteEnrollment ||
                statusKey === "completed" ||
                !!disabledActions.delete,
            },
          ],
          ...(fromGroup
            ? [
                [
                  {
                    label: String(t("card.actions.edit-group-enrollment")),
                    icon: <Pencil size={14} />,
                    onClick: () => onEditGroupEnrollment?.(user),
                    disabled:
                      !onEditGroupEnrollment ||
                      !user.groupType ||
                      !!disabledActions.editGroup,
                  },
                  {
                    label: String(t("card.actions.delete-group-enrollment")),
                    icon: <Trash2 size={14} />,
                    onClick: () => onDeleteGroupEnrollment?.(user),
                    disabled:
                      !onDeleteGroupEnrollment ||
                      !user.groupType ||
                      !!disabledActions.deleteGroup,
                  },
                ],
              ]
            : []),
        ]
      : [];

  // P70: Storico e certificato non dipendono più da Elimina iscrizione.
  // Lo Storico compare solo se il chiamante passa onHistory (cioè se
  // l'utente ha il permesso); il certificato segue il proprio permesso (P73).
  const historyActions: ListItemAction[][] = [
    ...(onHistory
      ? [
          [
            {
              label: String(t("card.actions.enrollment-history")),
              icon: <History size={14} />,
              onClick: () => onHistory(user),
              disabled: !!disabledActions.history,
            },
          ],
        ]
      : []),
    ...(showCertificateAction
      ? [
          [
            {
              label: String(t("card.actions.download-certificate")),
              icon: <Download size={14} />,
              onClick: () => onDownloadCertificate?.(user),
              disabled:
                !onDownloadCertificate ||
                !user.certificateIds?.length ||
                !!disabledActions.certificate,
            },
          ],
        ]
      : []),
    ...(onRefreshCertificate
      ? [
          [
            {
              label: String(t("card.actions.refresh-certificate")),
              icon: <RotateCcw size={14} />,
              onClick: () => onRefreshCertificate(user),
              disabled:
                getIssuedCertificateIds(user).length === 0 ||
                !!disabledActions.refreshCertificate,
            },
          ],
        ]
      : []),
  ];

  const moreActions: ListItemAction[][] = [
    ...enrollmentActions,
    ...solicitActions,
    ...progressActions,
    ...historyActions,
  ];

  return (
    <ListItemCardCst
      title={fullName}
      subtitle={{
        id: user.userId,
        email: user.userEmail,
        groupId: fromGroup ? String(user.massiveGroupId) : undefined,
        sessionIdEnrolled: user.editionId,
      }}
      details={{
        row1: `${t("card.completion-date")}: ${formatEnrollmentDate(user.completeDate)}`,
        row2: `${t("card.expiration-date")}: ${formatEnrollmentDate(user.expirationDate)}`,
      }}
      badges={badges}
      actions={
        onDetails
          ? [
              {
                label: String(t("card.details")),
                variant: "outlined",
                onClick: () => onDetails(user),
              },
            ]
          : undefined
      }
      moreActions={moreActions}
      activityType={activityType}
    />
  );
};

export default UserEnrollmentRow;
